import { type QueryReference, useReadQuery } from '@apollo/client'
import { Stack } from '@mui/material'
import { BottomNavigation as CandidateBottomNavigation } from 'candidate/components/BottomNavigation'
import Sidebar from 'candidate/components/Sidebar'
import { candidateRouteConfig } from 'candidate/routes'
import { ClientTopNavigation } from 'client/components/TopNavigation'
import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, Outlet, useLoaderData, useLocation } from 'react-router-dom'
import { BottomNavigation as RecruiterBottomNavigation } from 'recruiter/components/BottomNavigation'
import { RecruiterTopNavigation } from 'recruiter/components/TopNavigation'
import BreadCrumb from 'shared/components/BreadCrumb'
import useIsMobile from 'shared/hooks/useIsMobile'
import { useNotifications } from 'shared/hooks/useNotification'
import { UserRole } from 'shared/models/types/User'
import { commonRouteConfig, noBreadcrumbPaths } from 'shared/routeConfig'
import 'shared/style/App.scss'
import { getPxAsRem } from 'shared/utils'
import { getUserQueryResults } from '../../graphql/user.gql'
import { setUser } from '../../state/userSlice'
import { getSubdomain } from '../../utils/routing'
import { ImpersonationBanner } from '../ImpersonationBanner'
import { RecruiterPortalProviders } from './RecruiterPortalProviders'

export function RouteContainer() {
    const dispatch = useDispatch()
    const queryRef = useLoaderData()
    const location = useLocation()
    const { checkNotifications } = useNotifications()
    const isMobile = useIsMobile()
    const { data } = useReadQuery<getUserQueryResults>(
        queryRef as QueryReference<getUserQueryResults>
    )

    useEffect(() => {
        checkNotifications()
    }, [checkNotifications])

    useEffect(() => {
        if (data.userMe) dispatch(setUser(data.userMe))
    }, [data, dispatch])

    const subdomain = getSubdomain(process.env.REACT_APP_DOMAIN)

    if (
        data.userMe.isActive &&
        location.pathname === commonRouteConfig.index.path
    ) {
        const defaultRoute = (() => {
            switch (subdomain) {
                case UserRole.recruiter:
                    return '/research'
                case UserRole.candidate:
                    return '/dashboard'
                case UserRole.client:
                    return '/searches'
                default:
                    return '/'
            }
        })()
        return <Navigate to={defaultRoute} />
        // TODO: read from route configs based on role once we have the dashboard path for each role
    }

    const isIntakePage = location.pathname.startsWith(
        `/${candidateRouteConfig.intakes.basePath}`
    )

    switch (subdomain) {
        case UserRole.candidate:
            return (
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    height="100vh"
                    minWidth="100vw"
                    overflow="hidden"
                >
                    <ImpersonationBanner />
                    <Sidebar />
                    <MainContent />
                    {isMobile && !isIntakePage && <CandidateBottomNavigation />}
                </Stack>
            )
        case UserRole.recruiter:
            return (
                <RecruiterPortalProviders>
                    <Stack height="100vh" minWidth="100vw" overflow="hidden">
                        <RecruiterTopNavigation />
                        <MainContent />
                        {isMobile && <RecruiterBottomNavigation />}
                    </Stack>
                </RecruiterPortalProviders>
            )
        case UserRole.client:
            return (
                <Stack height="100vh" minWidth="100vw" overflow="hidden">
                    <ImpersonationBanner />
                    <ClientTopNavigation />
                    <MainContent />
                </Stack>
            )
        default:
            return 'Unknown subdomain'
    }
}

function MainContent() {
    const location = useLocation()

    const shouldShowBreadcrumbs = useMemo(
        () =>
            !noBreadcrumbPaths.some((noBreadcrumbPath) =>
                location.pathname.startsWith(noBreadcrumbPath)
            ),
        [location.pathname]
    )

    return (
        <Stack
            id="main"
            component="main"
            sx={{
                container: 'main / size',
                flex: 1,
                isolation: 'isolate',
                overflowY: 'auto',

                // fixes issue with the input field resizing on hover
                // related to https://github.com/mui/material-ui/pull/42452 ?
                // could maybe be improved somewhat still but it's way better than without this
                '.MuiAutocomplete-input': {
                    minWidth: `${getPxAsRem(30)} !important`,
                },
            }}
        >
            {shouldShowBreadcrumbs && <BreadCrumb />}
            <Outlet />
        </Stack>
    )
}
